<!--
/**
 * FaceEnrollment.vue
 * Admin page to enroll employee face descriptors using AI
 */
-->
<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        🤖 Pendaftaran Wajah Karyawan
      </oxd-text>
      <oxd-text tag="p" class="fe-subtitle">
        Daftarkan wajah karyawan untuk verifikasi absensi berbasis AI
      </oxd-text>

      <oxd-divider />

      <div class="fe-layout">
        <!-- Left: Employee List -->
        <div class="fe-employee-panel">
          <div class="fe-search-box">
            <oxd-input-field
              v-model="searchQuery"
              placeholder="🔍 Cari karyawan..."
              @input="filterEmployees"
            />
          </div>

          <div class="fe-employee-list">
            <div v-if="isLoadingEmployees" class="fe-loading">
              <oxd-loading-spinner />
            </div>
            <div
              v-for="emp in filteredEmployees"
              :key="emp.empNumber"
              class="fe-employee-item"
              :class="{
                'fe-employee-item--selected':
                  selectedEmployee?.empNumber === emp.empNumber,
                'fe-employee-item--enrolled': emp.enrolled,
              }"
              @click="selectEmployee(emp)"
            >
              <div class="fe-emp-avatar">
                {{ emp.firstName.charAt(0) }}{{ emp.lastName.charAt(0) }}
              </div>
              <div class="fe-emp-info">
                <div class="fe-emp-name">
                  {{ emp.firstName }} {{ emp.lastName }}
                </div>
                <div class="fe-emp-id">ID: {{ emp.empNumber }}</div>
              </div>
              <div class="fe-emp-status">
                <span v-if="emp.enrolled" class="fe-badge fe-badge--enrolled"
                  >✓ Terdaftar</span
                >
                <span v-else class="fe-badge fe-badge--pending">Belum</span>
              </div>
            </div>
          </div>

          <!-- Stats -->
          <div class="fe-stats">
            <div class="fe-stat">
              <div class="fe-stat-num">{{ enrolledCount }}</div>
              <div class="fe-stat-label">Terdaftar</div>
            </div>
            <div class="fe-stat">
              <div class="fe-stat-num">
                {{ employees.length - enrolledCount }}
              </div>
              <div class="fe-stat-label">Belum</div>
            </div>
            <div class="fe-stat">
              <div class="fe-stat-num">{{ employees.length }}</div>
              <div class="fe-stat-label">Total</div>
            </div>
          </div>
        </div>

        <!-- Right: Enrollment Panel -->
        <div class="fe-enroll-panel">
          <div v-if="!selectedEmployee" class="fe-empty-state">
            <div class="fe-empty-icon">👈</div>
            <oxd-text tag="p"
              >Pilih karyawan dari daftar sebelah kiri untuk mendaftarkan
              wajahnya</oxd-text
            >
          </div>

          <template v-else>
            <div class="fe-selected-info">
              <div class="fe-selected-avatar">
                {{ selectedEmployee.firstName.charAt(0)
                }}{{ selectedEmployee.lastName.charAt(0) }}
              </div>
              <div>
                <div class="fe-selected-name">
                  {{ selectedEmployee.firstName }}
                  {{ selectedEmployee.lastName }}
                </div>
                <div class="fe-selected-id">
                  Employee ID: {{ selectedEmployee.empNumber }}
                </div>
                <div v-if="selectedEmployee.enrolled" class="fe-enrolled-date">
                  Terdaftar sejak: {{ selectedEmployee.enrolledAt || '-' }}
                </div>
              </div>
            </div>

            <div
              class="fe-enrollment-status"
              :class="
                selectedEmployee.enrolled ? 'fe-es--enrolled' : 'fe-es--pending'
              "
            >
              <span>{{
                selectedEmployee.enrolled
                  ? '✅ Wajah sudah terdaftar — Klik Perbarui untuk re-enroll'
                  : '⏳ Wajah belum terdaftar'
              }}</span>
            </div>

            <!-- Face Recognition Component -->
            <face-recognition
              :emp-number="selectedEmployee.empNumber"
              mode="enroll"
              @enrolled="onEnrolled"
              @camera-error="onCameraError"
            />

            <!-- Delete enrollment -->
            <div v-if="selectedEmployee.enrolled" class="fe-delete-section">
              <oxd-button
                label="🗑 Hapus Data Wajah"
                display-type="danger-outline"
                @click="deleteEnrollment"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <delete-confirmation ref="deleteDialog" />
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {OxdSpinner} from '@ohrm/oxd';
import FaceRecognition from '@/orangehrmAttendancePlugin/components/FaceRecognition.vue';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';

export default {
  name: 'FaceEnrollment',

  components: {
    'face-recognition': FaceRecognition,
    'oxd-loading-spinner': OxdSpinner,
    'delete-confirmation': DeleteConfirmationDialog,
  },

  setup() {
    const empHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/employees',
    );
    const faceHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/face-descriptors',
    );
    return {empHttp, faceHttp};
  },

  data() {
    return {
      employees: [],
      filteredEmployees: [],
      selectedEmployee: null,
      isLoadingEmployees: false,
      searchQuery: '',
      enrolledEmpNumbers: new Set(),
    };
  },

  computed: {
    enrolledCount() {
      return this.employees.filter((e) => e.enrolled).length;
    },
  },

  async created() {
    await this.loadData();
  },

  methods: {
    async loadData() {
      this.isLoadingEmployees = true;
      try {
        // Load employees
        const empRes = await this.empHttp.getAll({limit: 1000});
        const employees = empRes.data.data.map((e) => ({
          empNumber: e.empNumber,
          firstName: e.firstName,
          lastName: e.lastName,
          enrolled: false,
          enrolledAt: null,
        }));

        // Load enrolled descriptors
        const faceRes = await this.faceHttp.getAll();
        const enrolledMap = {};
        faceRes.data.data.forEach((d) => {
          enrolledMap[d.empNumber] = d.enrolledAt;
        });

        // Merge
        this.employees = employees.map((e) => ({
          ...e,
          enrolled: !!enrolledMap[e.empNumber],
          enrolledAt: enrolledMap[e.empNumber] || null,
        }));

        this.filteredEmployees = [...this.employees];
      } finally {
        this.isLoadingEmployees = false;
      }
    },

    filterEmployees() {
      const q = this.searchQuery.toLowerCase();
      this.filteredEmployees = this.employees.filter(
        (e) =>
          e.firstName.toLowerCase().includes(q) ||
          e.lastName.toLowerCase().includes(q) ||
          String(e.empNumber).includes(q),
      );
    },

    selectEmployee(emp) {
      this.selectedEmployee = {...emp};
    },

    onEnrolled() {
      this.$toast.saveSuccess();
      // Update enrolled status in list
      const emp = this.employees.find(
        (e) => e.empNumber === this.selectedEmployee.empNumber,
      );
      if (emp) {
        emp.enrolled = true;
        emp.enrolledAt = new Date()
          .toISOString()
          .slice(0, 19)
          .replace('T', ' ');
      }
      this.selectedEmployee = {...emp};
      this.filteredEmployees = [...this.employees];
    },

    onCameraError(err) {
      this.$toast.cannotSave();
      console.error('[FaceEnrollment] Camera error:', err);
    },

    async deleteEnrollment() {
      const confirmed = await this.$refs.deleteDialog.showDialog();
      if (confirmed === 'ok') {
        await this.faceHttp.deleteById([this.selectedEmployee.empNumber]);
        this.$toast.deleteSuccess();
        const emp = this.employees.find(
          (e) => e.empNumber === this.selectedEmployee.empNumber,
        );
        if (emp) {
          emp.enrolled = false;
          emp.enrolledAt = null;
        }
        this.selectedEmployee = {...emp};
        this.filteredEmployees = [...this.employees];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fe-subtitle {
  color: #607d8b;
  font-size: 13px;
  margin-top: 4px;
  margin-bottom: 0;
}

.fe-layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 24px;
  min-height: 560px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

// ─── Employee Panel ───────────────────────────────────────────────────────────
.fe-employee-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-right: 1px solid #e0e0e0;
  padding-right: 24px;
}

.fe-search-box {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.fe-employee-list {
  flex: 1;
  overflow-y: auto;
  max-height: 420px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fe-loading {
  display: flex;
  justify-content: center;
  padding: 24px;
}

.fe-employee-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1.5px solid transparent;

  &:hover {
    background: #f5f5f5;
  }

  &--selected {
    background: #e3f2fd !important;
    border-color: #42a5f5 !important;
  }

  &--enrolled .fe-emp-name {
    color: #2e7d32;
  }
}

.fe-emp-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1565c0, #42a5f5);
  color: white;
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  text-transform: uppercase;
}

.fe-emp-info {
  flex: 1;
  min-width: 0;
}

.fe-emp-name {
  font-weight: 600;
  font-size: 13px;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fe-emp-id {
  font-size: 11px;
  color: #90a4ae;
}

.fe-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 10px;
  white-space: nowrap;

  &--enrolled {
    background: #e8f5e9;
    color: #2e7d32;
  }

  &--pending {
    background: #fff3e0;
    color: #e65100;
  }
}

.fe-stats {
  display: flex;
  border-top: 1px solid #e0e0e0;
  padding-top: 12px;
  gap: 8px;
}

.fe-stat {
  flex: 1;
  text-align: center;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 8px;
}

.fe-stat-num {
  font-size: 20px;
  font-weight: 700;
  color: #1565c0;
}

.fe-stat-label {
  font-size: 11px;
  color: #78909c;
}

// ─── Enroll Panel ─────────────────────────────────────────────────────────────
.fe-enroll-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.fe-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 400px;
  color: #90a4ae;
  text-align: center;
}

.fe-empty-icon {
  font-size: 48px;
}

.fe-selected-info {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #f8f9fa;
  border-radius: 12px;
}

.fe-selected-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1565c0, #42a5f5);
  color: white;
  font-weight: 700;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  flex-shrink: 0;
}

.fe-selected-name {
  font-weight: 700;
  font-size: 16px;
  color: #263238;
}

.fe-selected-id {
  font-size: 12px;
  color: #78909c;
}

.fe-enrolled-date {
  font-size: 11px;
  color: #4caf50;
  margin-top: 2px;
}

.fe-enrollment-status {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;

  &.fe-es--enrolled {
    background: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #a5d6a7;
  }

  &.fe-es--pending {
    background: #fff8e1;
    color: #e65100;
    border: 1px solid #ffcc80;
  }
}

.fe-delete-section {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
  border-top: 1px solid #f5f5f5;
}
</style>
